import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { DataProviderWrapper } from "@/lib/data-provider/context";
import { DemoDataProvider } from "@/lib/data-provider/demo-data-provider";
import { RoomsProvider } from "@/lib/contexts/rooms-context";
import { ArrowLeft, Sparkles } from "lucide-react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

// Import the demo sidebar
import { DemoAppSidebar } from "./demo-app-sidebar";

const demoDataProvider = new DemoDataProvider();

const breadcrumbMap: Record<string, string> = {
  "/demo/owner/dashboard": "Dashboard",
  "/demo/owner/bookings": "Rentals",
  "/demo/owner/analytics": "Analytics",
  "/demo/owner/payouts": "Payouts",
  "/demo/owner/settings": "Studio Settings",
  "/demo/owner/settings/basic": "Basic Info",
  "/demo/owner/settings/policies": "Policies",
  "/demo/owner/settings/operating-hours": "Operating Hours",
};

export function DemoOwnerLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  const getBreadcrumbTitle = () => {
    if (
      location.pathname.includes("/demo/owner/bookings/") &&
      location.pathname !== "/demo/owner/bookings"
    ) {
      return "Rental Details";
    }
    if (
      location.pathname.includes("/demo/owner/rooms/") &&
      location.pathname.includes("/availability")
    ) {
      return "Room Availability";
    }
    return breadcrumbMap[location.pathname] || "Dashboard";
  };

  return (
    <DataProviderWrapper provider={demoDataProvider}>
      <RoomsProvider>
        <SidebarProvider>
          <DemoAppSidebar />
          <SidebarInset>
            {/* Demo Mode Banner */}
            <div className="bg-surface-primary px-4 py-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="px-2 py-0.5 text-xs border-blue-jeans text-blue-jeans"
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    Demo Mode
                  </Badge>
                  <span className="text-xs text-on-dark-muted">
                    Exploring with sample data • No changes will be saved • Demo
                    has limited features
                  </span>
                </div>
                <Button
                  size="sm"
                  onClick={() => navigate("/join")}
                  className="text-xs"
                >
                  <ArrowLeft className="h-3 w-3 mr-1" />
                  Exit Demo
                </Button>
              </div>
            </div>

            {/* Regular Header */}
            <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
              <SidebarTrigger className="-ml-1" />
              <Separator orientation="vertical" className="mr-2 h-4" />
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem className="hidden md:block">
                    <BreadcrumbLink asChild>
                      <Link to="/demo/owner/dashboard">Demo Portal</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="hidden md:block" />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{getBreadcrumbTitle()}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </header>

            <div className="flex flex-1 flex-col gap-4 p-4">
              <Outlet />
            </div>
          </SidebarInset>
        </SidebarProvider>
      </RoomsProvider>
    </DataProviderWrapper>
  );
}
